import { create } from 'zustand';
import config from '../config';
import useSessionStore from './session';
import useAppStore from './app';
import localSessionData from './session-dummy-data';
import { NonV2RoomError } from '../helpers/errors';
import {
  CLIENT_TYPES,
  PROCTOR_ROOM_EVENTS,
  SyncClient,
} from '@smarterservices/sync-client';

const useDataBroker = create((set, get) => ({
  mode: 'remote',
  sync: {
    client: null,
    doc: null,
    emitter: null,
  },

  /** @type {Array<{event: string, handler: Function}>} Array of registered event listeners for cleanup */
  _eventListeners: [],

  initialize: async (mode) => {
    if (mode === 'remote') {
      // load data from the sync doc...

      const syncClientConfig = {
        apiUrl: config.proctoring.api.baseUrl,
        documentId: `Proctor_Room_${useAppStore.getState().roomSid}`,
        identity: `${useAppStore.getState().user.sid}${useAppStore.getState().roomSid}`,
        token: useAppStore.getState().user.token,
        type: CLIENT_TYPES.PROCTOR_ROOM,
      };

      //console.log(`Sync Client Config`, syncClientConfig);

      const localSync = { ...get().sync };

      localSync.client = await SyncClient(syncClientConfig);

      localSync.doc = await localSync.client.getSync();

      // attach the event emitter to the store...
      localSync.emitter = localSync.client.getEventEmitter();

      // set in state...
      set({ sync: localSync });

      // register the listeners
      get().setupListeners();

      // now call to load all the sessions...
      Object.entries(localSync.doc?.sessions || {}).forEach(([sessionSid, data]) => {
        useSessionStore.getState().add(sessionSid, data);
        // now, if the session is in review and it is by you, make that your focused session, this can happen during a refresh..
        if (data.location === 'review' && data.reviewBy === useAppStore.getState().user.sid) {
          useAppStore.getState().openDrawer('focus', sessionSid);
        }
      });

      // now add all the proctors that are in teh room.
      Object.entries(localSync.doc?.proctors || {}).forEach(
        ([proctorAccountUserSid, data]) => {
          useAppStore.getState().addProctor(proctorAccountUserSid, data);
        }
      );
    } else {
      // eslint-disable-next-line no-unused-vars
      Object.entries(localSessionData).forEach(([sessionSid, data]) => {
        useSessionStore.getState().add(sessionSid, data);
      });
    }

    return {
      successful: true,
      mode,
    };
  },

  setDataMode: (mode) => {
    set({ mode });
  },

  /**
   * Persists a session update to the sync document.
   *
   * @param {string} sessionSid - The session ID of the session to update.
   * @param {Object} data - The data associated with the session to update.
   * @returns {Promise} - Resolves with the result of the sync operation.
   */
  persist: async (sessionSid, data) => {
    // no not persist anything that has a null sessionSid
    if (!sessionSid || sessionSid === 'null' || sessionSid === null || sessionSid === undefined || sessionSid === '' || sessionSid === 'undefined' || sessionSid === '0') {
      return;
    }

    // if the session that is being cleaned up is the one attempting to have data updated....
    if (sessionSid === useSessionStore.getState().cleaningUpSessionSid) {
      return;
    }

    const dataToUpdate = {
      sessions: {
        [sessionSid]: data,
      },
    };
    const result = await get().sync.client.setSync(dataToUpdate);
    return result;
  },

  /**
   * Remove a field from the proctor room document. This is a promise
   * that resolves with the result of the delete operation.
   * @param {string} path - The dotted path of the field to remove.
   * @returns {Promise} - Resolves with the result of the delete operation.
   */
  remove: async (path) => {
    const result = await get().sync.client.deleteFieldByDottedPath(path);
    return result;
  },

  /**
   * Sets up event listeners for various proctor room events.
   *
   * This function initializes listeners on the sync emitter for handling
   * different student-related events within the proctor room. It listens
   * to events such as when a student enters, transitions between states,
   * or exits the room, and updates the session store accordingly.
   *
   * Specifically, it handles the following events:
   * - STUDENT_ENTERED: Adds the student session to the store when a student joins the room.
   * - STUDENT_TRANSITIONED: Updates the student's location in the store when a student transitions states.
   * - STUDENT_EXITED: Removes the student's session from the store when a student exits the room.
   */

  setupListeners: () => {
    const listeners = [];
    // Student Joins room...
    const studentEnteredHandler = function (student) {
      useSessionStore.getState().add(student.sessionSid, student.data);
    };
    get().sync.emitter.on(PROCTOR_ROOM_EVENTS.STUDENT_ENTERED, studentEnteredHandler);
    listeners.push({ event: PROCTOR_ROOM_EVENTS.STUDENT_ENTERED, handler: studentEnteredHandler });

    // student moved state...
    const studentTransitionedHandler = function (student) {

      // if the location is checkout... don't do anything as we want to have to ack this session first...
      if (student.data.location === 'checkout') {
        return;
      }

      useSessionStore
        .getState()
        .handleTransitionEvent(student.sessionSid, student.data.location);
    };
    get().sync.emitter.on(PROCTOR_ROOM_EVENTS.STUDENT_TRANSITIONED, studentTransitionedHandler);
    listeners.push({ event: PROCTOR_ROOM_EVENTS.STUDENT_TRANSITIONED, handler: studentTransitionedHandler });

    // student exited room...
    const studentExitedHandler = function (student) {
      // only go ahead and remove if they are in any other state other than checkin or testing..
      const sessionData = useSessionStore.getState().getSessionData(student.sessionSid);

      // Check if sessionData exists before accessing location
      // Session may have been optimistically removed from local state already
      if (!sessionData) {
        return;
      }
      // if the session is is in sentback, reconnect, or waiting, remove it...
      if (['sentback', 'reconnect', 'waiting'].includes(sessionData.location)) {
        useSessionStore.getState().handleRemoveEvent(student.sessionSid);
      }
    };
    // Jason: Commenting out b/c I don't think we need to listen for any of this...
    get().sync.emitter.on(PROCTOR_ROOM_EVENTS.STUDENT_EXITED, studentExitedHandler);
    listeners.push({ event: PROCTOR_ROOM_EVENTS.STUDENT_EXITED, handler: studentExitedHandler });

    // Proctor exited the room...
    const proctorExitedHandler = function (event) {
      // if the proctor that exited was the currently logged in, log them out...
      if (
        event.proctorUserSid === useAppStore.getState().user.sid &&
        useAppStore.getState().room
      ) {
        useAppStore.getState().logout({
          loginScreenMessage:
            'You were automatically logged out. Please log in again.',
        });
      } else {
        useAppStore.getState().removeProctor(event.proctorUserSid);
      }
    };
    get().sync.emitter.on(PROCTOR_ROOM_EVENTS.PROCTOR_EXITED, proctorExitedHandler);
    listeners.push({ event: PROCTOR_ROOM_EVENTS.PROCTOR_EXITED, handler: proctorExitedHandler });

    // proctor entered room...
    const proctorEnteredHandler = function (event) {
      useAppStore.getState().addProctor(event.proctorUserSid, event.data);
    };
    get().sync.emitter.on(PROCTOR_ROOM_EVENTS.PROCTOR_ENTERED, proctorEnteredHandler);
    listeners.push({ event: PROCTOR_ROOM_EVENTS.PROCTOR_ENTERED, handler: proctorEnteredHandler });

    // Proctor data updated...
    const proctorUpdatedHandler = function (event) {
      useAppStore.getState().addProctor(event.proctorUserSid, event.data);
    };
    get().sync.emitter.on(PROCTOR_ROOM_EVENTS.PROCTOR_UPDATED, proctorUpdatedHandler);
    listeners.push({ event: PROCTOR_ROOM_EVENTS.PROCTOR_UPDATED, handler: proctorUpdatedHandler });



    const syncDocUpdatedHandler = function (event) {
      useSessionStore.getState().remoteDataUpdated(event.currentData, event.previousData);
    };

    get().sync.emitter.on(PROCTOR_ROOM_EVENTS.SYNC_DOC_UPDATED_WITH_PREVIOUS_DATA, syncDocUpdatedHandler);
    listeners.push({ event: PROCTOR_ROOM_EVENTS.SYNC_DOC_UPDATED_WITH_PREVIOUS_DATA, handler: syncDocUpdatedHandler });

    // Store listener references for cleanup
    set({ _eventListeners: listeners });
  },

  /**
   * Cleans up all event listeners and resets the data broker state.
   * 
   * This function should be called when leaving a room or unmounting the VTC
   * to prevent memory leaks from accumulated event listeners.
   * 
   * @function cleanup
   * @returns {void}
   * 
   * @example
   * // Call when leaving a room
   * useDataBroker.getState().cleanup();
   * 
   * @example
   * // Call in component cleanup
   * useEffect(() => {
   *   return () => {
   *     useDataBroker.getState().cleanup();
   *   };
   * }, []);
   */
  cleanup: () => {
    const listeners = get()._eventListeners;
    const emitter = get().sync.emitter;

    // Remove all registered event listeners
    if (listeners && listeners.length > 0 && emitter) {
      listeners.forEach(({ event, handler }) => {
        try {
          emitter.off(event, handler);
        } catch (error) {
          console.error(`Failed to remove listener for event: ${event}`, error);
        }
      });
    }

    // Reset state
    set({
      _eventListeners: [],
      sync: {
        client: null,
        doc: null,
        emitter: null,
      },
    });
  },
}));

export default useDataBroker;
